import React, { useState } from 'react';
import { Formik, Form, Field, FieldArray } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { CommonHash, Landing, ApiObject } from 'app/types';

import Text from 'common/components/Text/Text';
import {
  useAddLandingMutation,
  useGetBackofficeNewLandingQuery,
  useGetBackofficeEditLandingQuery,
} from 'services/exploreApi';
import Loader from 'common/components/Loader/Loader';
import styles from './LandingForm.module.scss';
import LandingComponentForm from './LandingComponentForm';
import { useParams } from 'react-router-dom';

const LandingSchema = Yup.object().shape({
  slug: Yup.string().required('Required'),
  name: Yup.string().required('Required'),
  landing_components: Yup.array().of(
    Yup.object().shape({
      size: Yup.string().required('Required'),
      gap: Yup.string().required('Required'),
      background_size: Yup.string().required('Required'),
    })
  ),
});

const LandingForm = (): React.ReactElement => {
  const navigate = useNavigate();
  const { id } = useParams();
  let landing: ApiObject<Landing> | undefined;
  let isLandingLoading: boolean;
  if (id) {
    ({ data: landing, isLoading: isLandingLoading } = useGetBackofficeEditLandingQuery(id));
  } else {
    ({ data: landing, isLoading: isLandingLoading } = useGetBackofficeNewLandingQuery());
  }
  const [addLanding, { isLoading: isLoadingSubmit }] = useAddLandingMutation();
  const initialValues: Landing = { ...(landing?.data || ({ landing_path: true } as Landing)) };
  const [publishButton, setPublishButton] = useState(false);
  const [unpublishButton, setUnpublishButton] = useState(false);
  const [rendererKey, setRendererKey] = useState(0);
  if (isLandingLoading) return <Loader />;
  return (
    <div className={styles.landingFormContainer}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={LandingSchema}
        onSubmit={async (values) => {
          const formData = new FormData();
          formData.append(`landing[name]`, values.name);
          formData.append(`landing[slug]`, values.slug);
          formData.append(`landing[meta_title]`, values.meta_title);
          formData.append(`landing[meta_description]`, values.meta_description);
          formData.append(`landing[status]`, values.status);
          formData.append(`landing[landing_path]`, String(values.landing_path));
          formData.append(`landing[is_homepage]`, String(values.is_homepage));
          values.landing_components.map((landing_cmp, index) => {
            formData.append(`landing[landing_components[${index}][id]]`, String(landing_cmp.id));
            formData.append(`landing[landing_components[${index}][html_id]]`, landing_cmp.html_id);
            formData.append(
              `landing[landing_components[${index}][class_name]]`,
              landing_cmp.class_name
            );
            formData.append(`landing[landing_components[${index}][size]]`, landing_cmp.size);
            formData.append(`landing[landing_components[${index}][gap]]`, landing_cmp.gap);
            formData.append(
              `landing[landing_components[${index}][background_size]]`,
              landing_cmp.background_size
            );
            formData.append(
              `landing[landing_components[${index}][_destroy]]`,
              String(landing_cmp._destroy)
            );
            formData.append(
              `landing[landing_components[${index}][component_type]]`,
              landing_cmp.component_type
            );
            if (landing_cmp.background_image) {
              formData.append(
                `landing[landing_components[${index}][background_image]]`,
                landing_cmp.background_image
              );
            }
            formData.append(
              `landing[landing_components[${index}][background]]`,
              landing_cmp.background
            );
            formData.append(
              `landing[landing_components[${index}][config]]`,
              JSON.stringify(landing_cmp.config)
            );
            formData.append(
              `landing[landing_components[${index}][landing_component_buttons]]`,
              JSON.stringify(landing_cmp.landing_component_buttons)
            );
            landing_cmp.sub_landing_components.map((sub_landing_cmp, index2) => {
              formData.append(
                `landing[landing_components[${index}][sub_landing_components][${index2}][config]]`,
                JSON.stringify(sub_landing_cmp.config)
              );
              formData.append(
                `landing[landing_components[${index}][sub_landing_components][${index2}][id]]`,
                String(sub_landing_cmp.id)
              );
              formData.append(
                `landing[landing_components[${index}][sub_landing_components][${index2}][_destroy]`,
                String(sub_landing_cmp._destroy)
              );
              if (sub_landing_cmp.image_file) {
                formData.append(
                  `landing[landing_components[${index}][sub_landing_components][${index2}][image]]`,
                  sub_landing_cmp.image_file
                );
              }
            });
            if (landing_cmp.image_file) {
              formData.append(
                `landing[landing_components[${index}][image]]`,
                landing_cmp.image_file
              );
            }
          });
          const result = await addLanding({ data: formData });
          if (result) {
            navigate(`/backoffice/landings`);
          }
        }}
      >
        {({ setFieldValue, values, handleChange, errors, touched }) => (
          <Form className={styles.form}>
            <div className={`d-flex align-items-center gap-3 ${styles.header}`}>
              <h1>{id ? 'Edit Landing' : 'New Landing'}</h1>
              {values.published && (
                <Button
                  variant="danger"
                  onClick={() => {
                    setFieldValue('status', 'archived');
                    setUnpublishButton(true);
                  }}
                  className="fw-bold"
                  type="submit"
                  disabled={isLoadingSubmit}
                >
                  {isLoadingSubmit && unpublishButton ? (
                    <div className={`spinner-border ${styles.loading}`} role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : (
                    'Unpublish'
                  )}
                </Button>
              )}
              <Button
                variant="primary"
                onClick={() => {
                  setFieldValue('status', 'published');
                  setPublishButton(true);
                }}
                className="fw-bold"
                type="submit"
                disabled={isLoadingSubmit}
              >
                {isLoadingSubmit && publishButton ? (
                  <div className={`spinner-border ${styles.loading}`} role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                ) : (
                  'Save & Publish'
                )}
              </Button>
            </div>
            <div className={styles.group}>
              <label htmlFor="name">Name</label>
              <input
                name="name"
                placeholder="Name"
                onChange={handleChange}
                defaultValue={values.name || ''}
              />
              {errors.name && <div className={styles.error}>{errors.name}</div>}
            </div>
            <div className={styles.group}>
              <label htmlFor="slug">URL</label>
              <input
                name="slug"
                placeholder="Slug"
                onChange={handleChange}
                defaultValue={values.slug || ''}
              />
              {errors.slug && <div className={styles.error}>{errors.slug}</div>}
            </div>
            <div className={styles.group}>
              <label htmlFor="meta_title">Meta Title</label>
              <input
                name="meta_title"
                placeholder="Meta title"
                onChange={handleChange}
                defaultValue={values.meta_title || ''}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="meta_description">Meta Description</label>
              <input
                name="meta_description"
                placeholder="Meta description"
                onChange={handleChange}
                defaultValue={values.meta_description || ''}
              />
            </div>
            <div className={styles.group}>
              <label htmlFor="landing_path">Landing path</label>
              <Field name={`landing_path`} type="checkbox" defaultChecked={values.landing_path} />
            </div>

            <div className={styles.group}>
              <label htmlFor="landing_path">Is Homepage</label>
              <Field name={`is_homepage`} type="checkbox" defaultChecked={values.is_homepage} />
            </div>

            <Text size={24} weight={800}>
              Landing Components
            </Text>
            <FieldArray
              name="landing_components"
              render={(arrayHelpers) => (
                <div>
                  {values.landing_components && values.landing_components.length > 0 ? (
                    values.landing_components.map(
                      (landing_cmp: CommonHash, index: number) =>
                        !landing_cmp._destroy && (
                          <div key={index} className={styles.landingComponentItem}>
                            <LandingComponentForm
                              index={index}
                              landing_cmp={landing_cmp}
                              setFieldValue={setFieldValue}
                              handleChange={handleChange}
                              arrayHelpers={arrayHelpers}
                              rendererKey={rendererKey}
                              setRendererKey={setRendererKey}
                            />
                          </div>
                        )
                    )
                  ) : (
                    <Button
                      type="button"
                      variant="success"
                      onClick={() => {
                        setRendererKey(rendererKey + 1);
                        arrayHelpers.push({
                          html_id: '',
                          class_name: '',
                          size: 'md',
                          gap: 'md',
                          background_size: 'contained',
                          background: 'white',
                          component_type: 'hero_banner',
                          config: {
                            title: '',
                            style: 'normal',
                            alignment: 'left',
                            section_title_color: 'default',
                            badge_color: 'primary',
                            title_type: 'default',
                            title_size: 'default',
                            title_color: 'default',
                            description_size: 'default',
                            description_color: 'default',
                          },
                          landing_component_buttons: [
                            {
                              text: '',
                              link: '',
                              style: 'primary',
                              size: 'default',
                            },
                          ],
                          sub_landing_components: [
                            {
                              config: {
                                icon_name: '',
                                icon_style: 'on_top',
                                card_title: '',
                                card_text: '',
                                card_background: 'transparent',
                              },
                            },
                          ],
                        });
                      }}
                    >
                      + Landing Component
                    </Button>
                  )}
                </div>
              )}
            />
            <button type="submit">Save Landing Form</button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default LandingForm;
